import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import RegistrationHeader from './components/RegistrationHeader';
import RegistrationForm from './components/RegistrationForm';
import PlatformBenefits from './components/PlatformBenefits';

const UserRegistration = () => {
  const [user, setUser] = useState(null);

  const handleRegister = (newUser) => {
    setUser(newUser);
    // In a real app, this would also update global auth state
    localStorage.setItem('seleniumSandboxUser', JSON.stringify(newUser));
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Selenium Sandbox | Interactive Automation Learning</title>
        <meta name="description" content="Create your Selenium Sandbox account and start learning web automation with hands-on practice, real-time code execution, and mission-based progression." />
        <meta name="keywords" content="selenium, automation, testing, registration, signup, web development, QA, learning platform" />
        <meta property="og:title" content="Join Selenium Sandbox - Master Web Automation" />
        <meta property="og:description" content="Interactive learning platform for Selenium automation with live code execution and visual feedback." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={user} />
        
        <main className="content-offset">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
              {/* Mobile-first responsive layout */}
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
                
                {/* Left Column - Registration Form */}
                <div className="space-y-8">
                  <RegistrationHeader />
                  <RegistrationForm onRegister={handleRegister} />
                </div>

                {/* Right Column - Platform Benefits */}
                <div className="mt-12 lg:mt-0 lg:sticky lg:top-24">
                  <PlatformBenefits />
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-subtle py-8 mt-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                {/* Company Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Selenium Sandbox
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The premier platform for learning Selenium automation through 
                    interactive missions and real-time code execution.
                  </p>
                </div>

                {/* Quick Links */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Quick Links
                  </h4>
                  <div className="space-y-2">
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Terms of Service
                    </a>
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Privacy Policy
                    </a>
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Documentation
                    </a>
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Support
                    </a>
                  </div>
                </div>

                {/* Contact */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Get Help
                  </h4>
                  <div className="space-y-2">
                    <a href="mailto:support@seleniumsandbox.com" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      support@seleniumsandbox.com
                    </a>
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Community Forum
                    </a>
                    <a href="#" className="block text-xs text-muted-foreground hover:text-primary transition-colors">
                      Learning Resources
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-t border-subtle mt-8 pt-6 flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
                <p className="text-xs text-muted-foreground">
                  © {new Date()?.getFullYear()} Selenium Sandbox. All rights reserved.
                </p>
                <div className="flex items-center space-x-4">
                  <span className="text-xs text-muted-foreground">
                    Secure & SSL Protected
                  </span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-xs text-success">All Systems Operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default UserRegistration;